/**
 * KINDW-1598 / IM-005: Checkout inventory ids — derive from basket shipping address,
 * not global cookies that PDP may have written.
 */

import {useMemo} from 'react';

import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {useResolvedInventoryListId} from './use-resolved-inventory-list-id';
import {useShippingMethods} from './use-shipping-methods';
import {useShopperInventoryIds} from './use-shopper-inventory-ids';
import {
  formatInventoryIdsParam,
  isWarehouseResolutionPreferenceEnabled,
} from '../utils/warehouse-inventory-cookies';
import {
  getFirstWhsCodeFromMethods,
  getWhsCodeFromShippingMethod,
  isShippingServiceApiSource,
} from '../utils/shipping-method-whs-code';

/**
 * @returns {{
 *   shopperInventoryIds: string|undefined,
 *   isInventoryIdsResolving: boolean,
 * }}
 */
export function useCheckoutShopperInventoryIds() {
  const {data: basket} = useCurrentBasket();
  const {site} = useMultiSite();
  const preferences = site?.preferences || {};
  const cookieIds = useShopperInventoryIds();

  const shippingAddress = basket?.shipments?.[0]?.shippingAddress;
  const selectedMethodId = basket?.shipments?.[0]?.shippingMethod?.id;

  const hasBasketShipping = Boolean(
    shippingAddress?.postalCode?.trim() && shippingAddress?.countryCode?.trim()
  );

  const prefEnabled = isWarehouseResolutionPreferenceEnabled(preferences);
  const shippingQueryEnabled = Boolean(prefEnabled && hasBasketShipping && basket?.basketId);

  const {data: shippingData, isLoading: isShippingMethodsLoading} = useShippingMethods({
    cacheContext: 'checkout',
    enabled: shippingQueryEnabled,
  });

  const whsCode = useMemo(() => {
    const methods = shippingData?.applicableShippingMethods;

    if (!methods?.length) {
      return undefined;
    }

    if (selectedMethodId) {
      const selected = methods.find((m) => m.id === selectedMethodId);
      const fromSelected = getWhsCodeFromShippingMethod(selected);

      if (fromSelected) {
        return fromSelected;
      }
    }

    return getFirstWhsCodeFromMethods(methods);
  }, [shippingData?.applicableShippingMethods, selectedMethodId]);

  const shippingApiActive = isShippingServiceApiSource(shippingData?.source);

  const shouldResolveWarehouse = Boolean(
    prefEnabled && hasBasketShipping && shippingApiActive && whsCode
  );

  const {
    inventoryListId,
    isLoading: isStoreLoading,
    isFetching: isStoreFetching,
  } = useResolvedInventoryListId({
    whsCode,
    enabled: shouldResolveWarehouse,
  });

  const resolvedIds = formatInventoryIdsParam(inventoryListId);

  return useMemo(() => {
    if (!prefEnabled || !hasBasketShipping) {
      return {
        shopperInventoryIds: cookieIds,
        isInventoryIdsResolving: false,
      };
    }

    const isResolving =
      isShippingMethodsLoading ||
      (shippingApiActive && !whsCode) ||
      (shouldResolveWarehouse && (isStoreLoading || isStoreFetching));

    if (resolvedIds) {
      return {
        shopperInventoryIds: resolvedIds,
        isInventoryIdsResolving: false,
      };
    }

    if (isResolving) {
      return {
        shopperInventoryIds: undefined,
        isInventoryIdsResolving: true,
      };
    }

    return {
      shopperInventoryIds: cookieIds,
      isInventoryIdsResolving: false,
    };
  }, [
    prefEnabled,
    hasBasketShipping,
    cookieIds,
    resolvedIds,
    isShippingMethodsLoading,
    shippingApiActive,
    whsCode,
    shouldResolveWarehouse,
    isStoreLoading,
    isStoreFetching,
  ]);
}

export default useCheckoutShopperInventoryIds;
